import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnvelopeSimple, X, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/App';

const KINDS = [
  { id: 'verification',        label: 'Verification code',     desc: 'Registration 6-digit code (sample: 123456)' },
  { id: 'ticket_confirmation', label: 'Ticket confirmation',   desc: 'Sample ticket #1 purchase confirmation' },
  { id: 'password_reset',      label: 'Password reset',        desc: 'Reset link with a fake (safe) token' },
  { id: 'kyc_approved',        label: 'KYC approved',          desc: 'KYC review approval notice' },
  { id: 'kyc_rejected',        label: 'KYC rejected',          desc: 'KYC review rejection with sample reason' },
];

export default function EmailDiagnosticPanel({ token, smtpConfigured, defaultEmail = '' }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(defaultEmail);
  const [busyKind, setBusyKind] = useState(null);
  const [results, setResults] = useState({});

  const send = async (kind) => {
    const email = (recipient || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    setBusyKind(kind);
    setResults((r) => ({ ...r, [kind]: { status: 'pending' } }));
    try {
      const r = await fetch(`${API}/admin/email/diagnostic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ toEmail: email, kind }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setResults((rr) => ({ ...rr, [kind]: { status: 'error', detail: d.detail || `HTTP ${r.status}` } }));
        toast.error(`${kind}: ${d.detail || 'failed'}`);
      } else {
        setResults((rr) => ({ ...rr, [kind]: { status: 'ok', note: d.note } }));
        toast.success(`Sent ${kind} → ${email}`);
      }
    } catch (err) {
      setResults((rr) => ({ ...rr, [kind]: { status: 'error', detail: err.message } }));
      toast.error(`Network: ${err.message}`);
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <>
      <Button
        data-testid="email-diagnostic-button"
        onClick={() => setOpen(true)}
        disabled={!smtpConfigured}
        variant="outline"
        title={!smtpConfigured ? 'SMTP not configured' : 'Send sample of any transactional email'}
        className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10"
      >
        <EnvelopeSimple size={18} className="mr-2" />
        Email Diagnostics
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0e0b04]/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="bg-[#1a1508] border border-[#cba135]/30 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
              data-testid="email-diagnostic-modal"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#cba135]/15">
                <div className="flex items-center gap-3">
                  <EnvelopeSimple size={22} weight="duotone" className="text-[#cba135]" />
                  <div>
                    <h2 className="heading-font text-lg text-[#cba135]">Email Diagnostics</h2>
                    <p className="text-xs text-[#cba135]/55 mt-0.5">
                      Send sample of any transactional template to verify rendering in Gmail / Outlook / Apple Mail.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 text-[#cba135]/60 hover:text-[#cba135] hover:bg-[#cba135]/10 rounded transition-colors"
                  aria-label="Close"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="px-6 py-4 border-b border-[#cba135]/10 bg-[#0e0b04]/30">
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">
                  Recipient
                </label>
                <Input
                  data-testid="email-diagnostic-recipient"
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135]"
                />
                <p className="text-[11px] text-[#cba135]/45 mt-1.5">
                  Tip: use your real personal email so you can open it in Gmail, Outlook + iPhone Mail side by side.
                </p>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {KINDS.map((k) => {
                  const res = results[k.id];
                  return (
                    <div
                      key={k.id}
                      data-testid={`diag-row-${k.id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#cba135]/15 bg-[#0e0b04]/40"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#cba135]">{k.label}</p>
                        <p className="text-xs text-[#cba135]/55 mt-0.5">{k.desc}</p>
                        {res?.status === 'ok' && (
                          <p className="text-[11px] text-green-300/85 mt-1 flex items-center gap-1">
                            <CheckCircle size={12} weight="duotone" /> Sent · {res.note}
                          </p>
                        )}
                        {res?.status === 'error' && (
                          <p className="text-[11px] text-red-300/85 mt-1 flex items-center gap-1">
                            <WarningCircle size={12} weight="duotone" /> {res.detail}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!busyKind}
                        onClick={() => send(k.id)}
                        data-testid={`diag-send-${k.id}`}
                        className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10 shrink-0"
                      >
                        {busyKind === k.id ? 'Sending…' : 'Send'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="px-6 py-3 border-t border-[#cba135]/10 bg-[#0e0b04]/40">
                <p className="text-[11px] text-[#cba135]/55 leading-relaxed">
                  These are <span className="text-[#e6be4b]">sample messages with fake IDs</span> — they don&apos;t modify any real data.
                  Brevo daily quotas still apply; rate-limited to 20 sends per 15 min per admin.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
